import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { toast } from "react-toastify";
import { Col, Form, FormGroup, Label, Modal, ModalBody, Row } from 'reactstrap';
import moment from "moment";
import { Field, reduxForm, blur } from 'redux-form';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import ValidatedInput from "../../../components/ValidatedInput";
import ValidatedSelect from "../../../components/ValidatedSelect";
import { formatAmount, normalizeAmount } from "../../../utils/helper";
import { recurringDeposit, resetFlags } from '../../../redux/actions/AccountAction';

const RecurringDeposit = ({
  availableAccounts,
  handleSubmit,
  openModal = false,
  onClosed,
  recurringDeposit,
  resetFlags,
  accountErrors,
  accountFlags,
  onSuccess
}) => {
  const [modal, setModal] = useState(openModal);
  const [processing, setProcessing] = useState(false);
  const [noEndDate, setNoEndDate] = useState(false);

  useEffect(() => {
    setModal(openModal);
  }, [openModal]);

  useEffect(() => {
    if (accountErrors && accountErrors.recurringDeposit) {
      toast.error(accountErrors.plaidFundTransfer);
      setProcessing(false);
      resetFlags();
    }

    if (accountFlags && accountFlags.recurringDepositSuccess) {
      resetFlags();
      setProcessing(false);
      toggleModal();
      onSuccess();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountErrors, accountFlags]);

  const toggleModal = () => setModal(!modal);

  const handleChange = () => {
    blur('end_date');
    setNoEndDate(!noEndDate);
  }

  const availableFrequency = [
    {
      label: 'One-Time',
      value: 'One-Time',
    },
    {
      label: 'Monthly',
      value: 'Monthly',
    },
    {
      label: 'Every Other Month',
      value: 'Every Other Month',
    },
    {
      label: 'Quarterly',
      value: 'Quarterly',
    },
    {
      label: 'Every 6 Months',
      value: 'Every 6 Months',
    },
    {
      label: 'Annually',
      value: 'Annually',
    },
  ];

  const handleTransfer = (formData) => {
    setProcessing(true);
    if (formData.not_required_end_date) {
      delete formData.not_required_end_date;
    }
    recurringDeposit(formData);
  }

  const onModalClosed = () => {
    setModal(false);
    onClosed();
  }

  return (
    <Modal isOpen={modal} toggle={toggleModal} size="lg" onClosed={onModalClosed}>
      <ModalBody>
        <Form className="form-container" onSubmit={handleSubmit(handleTransfer)}>
          <Row>
            <Col sm={{ size: '3', offset: 9 }}>
              <div className="btn-save d-flex justify-content-end w-100">
                <button className="custom-btn-primary mb-2">
                  Save {processing && <FontAwesomeIcon icon={faSpinner} spin />}
                </button>
              </div>
            </Col>
          </Row>

          <h5>RECURRING DEPOSITS</h5>
          <FormGroup row>
            <Label sm={3}>Bank Account</Label>
            <Col sm={6}>
              <FormGroup className="mb-0">
                <Field
                  component={ValidatedSelect}
                  options={availableAccounts}
                  isSearchable={true}
                  isClearable={true}
                  name="bank_account_id"
                  placeholder="Select Account" />
              </FormGroup>
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label sm={3}>Frequency</Label>
            <Col sm={6}>
              <FormGroup className="mb-0">
                <Field
                  component={ValidatedSelect}
                  options={availableFrequency}
                  isSearchable={true}
                  isClearable={true}
                  name="frequency"
                  placeholder="Select Frequency" />
              </FormGroup>
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label sm={3}>Start Date</Label>
            <Col sm={6}>
              <Field
                component={ValidatedInput}
                type="date"
                name="start_date"
                minDate={new Date()}
                placeholder="Start Date" />
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label sm={3}>End Date</Label>
            <Col sm={6}>
              <Field
                component={ValidatedInput}
                type="date"
                name="end_date"
                minDate={new Date()}
                disabled={noEndDate}
                placeholder="End Date" />
            </Col>
            <Col sm={2} className="pr-0 checkbox">
              <FormGroup check>
                <Label check>
                  <Field
                    component={ValidatedInput}
                    onChange={handleChange}
                    type="checkbox"
                    name="not_required_end_date" />
                  Don't set an end date
                </Label>
              </FormGroup>
            </Col>
          </FormGroup>
          <FormGroup row>
            <Label sm={3}>Recurring Deposit</Label>
            <Col sm={6}>
              <Field
                component={ValidatedInput}
                type="text"
                format={formatAmount}
                normalize={normalizeAmount}
                prefix="$"
                name="recurring_deposit"
                placeholder="Recurring" />
            </Col>
          </FormGroup>
        </Form>
      </ModalBody>
    </Modal>
  )
};

const validate = (values) => {
  const errors = {};
  if (!values.bank_account_id) {
    errors.bank_account_id = 'Field is required';
  }
  if (!values.frequency) {
    errors.frequency = 'Field is required';
  }
  if (!values.start_date) {
    errors.start_date = 'Field is required';
  }
  if (!values.not_required_end_date && !values.end_date) {
    errors.end_date = 'Field is required';
  }
  if (!values.not_required_end_date && values.start_date && values.end_date) {
    const inDate = new Date(values.start_date);
    const outDate = new Date(values.end_date);
    const diff = moment(outDate).diff(moment(inDate), 'days');
    if (diff <= 0) {
      errors.end_date = 'Invalid date';
    }
  }
  if (!values.recurring_deposit) {
    errors.recurring_deposit = 'Field is required';
  }
  return errors;
};

const mapStateToProps = state => ({
  accountErrors: state.Account.errors,
  accountFlags: state.Account.flags,
  bankAccount: state.Account.bankAccount,
  plaidAccounts: state.Account.plaidAccounts,
});

const mapDispatchToProps = dispatch => ({
  recurringDeposit: payload => {
    dispatch(recurringDeposit(payload))
  },
  resetFlags: payload => {
    dispatch(resetFlags())
  }
});

const RecurringDepositComponent = connect(
  mapStateToProps,
  mapDispatchToProps
)(RecurringDeposit);

export default reduxForm({
  form: 'RecurringDepositForm',
  validate,
})(RecurringDepositComponent);
